import type { ZoneRole } from "../lib/zone-manifest";
import { ZONE_STYLE } from "../viewer/renderer";
import { useI18n } from "../i18n";
import { cn } from "./cn";

/**
 * WO-VRD-33 — Reveal Areas legend. A horizontal row that sits just above
 * the Territory bar while the fluorescent overlay is active. Hovering (or
 * tapping) a category isolates that zone on the grid via `onHoverRole`.
 * Swatches reuse the SAME `ZONE_STYLE` palette the canvas paints, so the
 * legend colours match the highlighted zones 1:1. Presentation-only.
 */
const LEGEND_KEYS: Array<{ role: ZoneRole; key: string }> = [
  { role: "LOCKED_GRID_ZERO", key: "gridZero" },
  { role: "VIP_CORE_SELLABLE", key: "vipCore" },
  { role: "PREMIUM", key: "premium" },
  { role: "LOCKED_RESERVE", key: "reserve" },
  { role: "STANDARD", key: "standard" },
];

export interface ZoneLegendProps {
  className?: string;
  activeRole?: ZoneRole | null;
  onHoverRole?: (role: ZoneRole | null) => void;
}

export function ZoneLegend({ className, activeRole, onHoverRole }: ZoneLegendProps) {
  const { t } = useI18n();
  const legend = LEGEND_KEYS.map(({ role, key }) => ({
    role,
    label: t(`grid.legend.${key}.label`),
    desc: t(`grid.legend.${key}.desc`),
  }));
  // Only wire hover-to-isolate on devices with a real hover pointer (desktop).
  // On touch, a synthetic `mouseenter` would fire on tap and race with the
  // click toggle (setting then clearing the role), so NO zone ever lights up.
  // Touch therefore relies solely on the click toggle below. On hover devices a
  // click keeps the hovered zone lit (defect B: it used to toggle it off).
  const canHover =
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;
  return (
    <div
      data-testid="zone-legend"
      onMouseLeave={canHover ? () => onHoverRole?.(null) : undefined}
      className={cn(
        "flex items-center justify-start md:justify-center gap-0.5 md:gap-1 flex-nowrap",
        "max-w-[calc(100vw-1rem)] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        "px-2 md:px-2.5 h-9 rounded-pill neon-glass pointer-events-auto select-none",
        className,
      )}
    >
      {legend.map((z) => {
        const style = ZONE_STYLE[z.role];
        const swatch = style.inner ?? style.border;
        const active = activeRole === z.role;
        return (
          <button
            key={z.role}
            type="button"
            data-testid={`zone-legend-item-${z.role}`}
            data-active={active ? "true" : "false"}
            title={z.desc}
            aria-label={`${z.label} — ${z.desc}`}
            onMouseEnter={canHover ? () => onHoverRole?.(z.role) : undefined}
            onFocus={canHover ? () => onHoverRole?.(z.role) : undefined}
            onBlur={canHover ? () => onHoverRole?.(null) : undefined}
            onClick={() => onHoverRole?.(canHover ? z.role : active ? null : z.role)}
            className={cn(
              "inline-flex items-center gap-1 md:gap-1.5 h-7 px-1.5 md:px-2 rounded-pill shrink-0",
              "transition-colors duration-150 focus:outline-none",
              "focus-visible:ring-1 focus-visible:ring-[color:var(--spec-cyan)]",
              active ? "bg-white/[0.06]" : "hover:bg-white/[0.04]",
            )}
          >
            <span
              aria-hidden
              className="h-2 w-2 md:h-2.5 md:w-2.5 shrink-0 rounded-[3px]"
              style={{
                background: swatch,
                border: `1px solid ${style.border}`,
                boxShadow: active ? `0 0 9px ${style.border}` : "none",
              }}
            />
            <span
              className={cn(
                "font-mono text-[9px] md:text-[10px] uppercase tracking-[0.04em] md:tracking-[0.1em] whitespace-nowrap",
                active ? "text-text-primary" : "text-text-secondary",
              )}
            >
              {z.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
